import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { UserFriendlyError } from '../../../model/Error'
import { getNextPageParam } from '../../../misc/query.utilities'
import { questionInstancesKeys } from './questionInstance.keys'
import { Filters } from '../../../model/Filters'
import { useFlatPaginatedData } from '../../utils/query/useFlatPaginatedData.hook'
import { usePaginationState } from '../../utils/query'
import { useCallback, useMemo } from 'react'
import { singleItemPageLimit, startingPage } from '../../../constants'
import { getQuestionInstances } from '../../../data/questionInstances'

interface GetInfiniteQuestionInstancesParams {
  enabled?: boolean
  filters?: Filters
  limit?: number
}

export const useGetInfiniteQuestionInstances = ({
  enabled = true,
  filters,
  limit = singleItemPageLimit,
}: GetInfiniteQuestionInstancesParams = {}) => {
  const queryKey = useMemo(() => questionInstancesKeys.list(filters), [filters])

  const queryReturn = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      const { data, ok } = await getQuestionInstances(pageParam, filters, signal, limit)
      if (!data || !ok) {
        throw new UserFriendlyError(
          'error',
          `Impossible de récupérer la liste des questions`,
          `Une erreur technique s'est produite`,
        )
      }
      return data
    },
    initialPageParam: startingPage,
    getNextPageParam,
    enabled,
  })

  const queryClient = useQueryClient()

  const questionList = useFlatPaginatedData(queryReturn.data)
  const paginationState = usePaginationState(queryReturn.data)

  const cancelPendingQuery = useCallback(() => {
    queryClient.cancelQueries({ queryKey })
  }, [queryClient, queryKey])

  return { query: queryReturn, questionList, paginationState, cancelPendingQuery }
}
